import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { Plus, Check, Trash2, Bell, UserPlus2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const isTeamLeadRole = (r) => ["admin", "manager", "director", "rm_and_above", "sales_manager"].includes(r);

export default function TodosPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ mine: [], assigned: [] });
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal", assigned_to: "" });
  const [tab, setTab] = useState("mine");

  const canAssign = isTeamLeadRole(user?.role);

  const load = async () => {
    const [t, u] = await Promise.all([
      api.get("/todos"),
      canAssign ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setData(t.data || { mine: [], assigned: [] });
    setUsers((u.data || []).filter((x) => x.id !== user?.id));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/todos", form);
      toast.success(form.assigned_to ? "Task assigned" : "Added");
      setOpen(false);
      setForm({ title: "", description: "", due_date: "", priority: "normal", assigned_to: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const toggle = async (t) => {
    await api.put(`/todos/${t.id}`, { status: t.status === "done" ? "pending" : "done" });
    load();
  };
  const del = async (id) => {
    try {
      await api.delete(`/todos/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const rows = tab === "mine" ? data.mine : data.assigned;
  const pending = rows.filter((t) => t.status !== "done");
  const done = rows.filter((t) => t.status === "done");

  const assignedPending = useMemo(
    () => (data.mine || []).filter((t) => t.status !== "done" && t.assigned_by),
    [data.mine]
  );

  return (
    <div>
      <PageHeader
        title="To-Do"
        subtitle="Your personal task list and tasks assigned to your team"
        actions={
          <Button onClick={() => setOpen((s) => !s)} data-testid="new-todo-btn">
            <Plus className="w-4 h-4" /> Add task
          </Button>
        }
      />

      {assignedPending.length > 0 && (
        <div className="mb-4 rounded-lg border border-[#DDA15E] bg-[#F2EDE4] p-4 flex items-start gap-3" data-testid="assigned-banner">
          <Bell className="w-5 h-5 text-[#C15A2C] shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#1A2E1C]">
              {assignedPending.length} task{assignedPending.length > 1 ? "s" : ""} assigned to you pending
            </div>
            <div className="text-[#5C6B5E]">Complete them so your manager can move on. This reminder appears every day until done.</div>
          </div>
        </div>
      )}

      {open && (
        <Card className="mb-6">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="todo-title" />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </Select>
            </div>
            {canAssign && (
              <div className="sm:col-span-2">
                <Label>Assign to (optional — leave blank for personal task)</Label>
                <Select
                  value={form.assigned_to}
                  onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  data-testid="todo-assign-to"
                >
                  <option value="">— Me (personal task) —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.role.replace(/_/g, " ")}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="todo-submit">
                {form.assigned_to ? <><UserPlus2 className="w-4 h-4" /> Assign</> : "Save"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setTab("mine")}
          data-testid="tab-mine"
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "mine" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
        >
          My tasks ({data.mine.length})
        </button>
        {canAssign && (
          <button
            onClick={() => setTab("assigned")}
            data-testid="tab-assigned"
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "assigned" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
          >
            Assigned by me ({data.assigned.length})
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-heading text-xl font-bold mb-3">Pending ({pending.length})</h3>
          {pending.length === 0 ? (
            <div className="text-sm text-[#5C6B5E]">Nothing pending here.</div>
          ) : (
            <ul className="space-y-2">
              {pending.map((t) => (
                <li key={t.id} className="flex items-start gap-3 border border-[#E5E2DC] rounded-lg p-3 hover:bg-[#F9F8F6]" data-testid={`todo-${t.id}`}>
                  {tab === "mine" ? (
                    <button onClick={() => toggle(t)} className="w-5 h-5 mt-0.5 border-2 border-[#1E3F20] rounded hover:bg-[#1E3F20] hover:text-white" data-testid={`toggle-${t.id}`} />
                  ) : (
                    <div className="w-5 h-5 mt-0.5 border-2 border-[#E5E2DC] rounded" />
                  )}
                  <div className="flex-1">
                    <div className="font-semibold">{t.title}</div>
                    {t.description && <div className="text-xs text-[#5C6B5E]">{t.description}</div>}
                    <div className="text-[11px] uppercase tracking-wider text-[#5C6B5E] mt-1 flex flex-wrap items-center gap-2">
                      {t.due_date && <span>Due {t.due_date}</span>}
                      <span>{t.priority}</span>
                      {tab === "mine" && t.assigned_by_name && (
                        <span className="text-[#C15A2C] font-bold">Assigned by {t.assigned_by_name}</span>
                      )}
                      {tab === "assigned" && (
                        <span className="text-[#5C6B5E]">→ {users.find((u) => u.id === t.user_id)?.name || "user"}</span>
                      )}
                    </div>
                  </div>
                  {(!t.assigned_by || t.assigned_by === user?.id) && (
                    <button onClick={() => del(t.id)} className="text-red-600 p-1 hover:bg-red-50 rounded" data-testid={`del-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <h3 className="font-heading text-xl font-bold mb-3">Completed ({done.length})</h3>
          {done.length === 0 ? (
            <div className="text-sm text-[#5C6B5E]">Nothing done yet.</div>
          ) : (
            <ul className="space-y-2">
              {done.map((t) => (
                <li key={t.id} className="flex items-start gap-3 border border-[#E5E2DC] rounded-lg p-3 bg-[#F2EDE4]/40">
                  {tab === "mine" ? (
                    <button onClick={() => toggle(t)} className="w-5 h-5 mt-0.5 rounded bg-[#1E3F20] text-white grid place-items-center">
                      <Check className="w-3 h-3" />
                    </button>
                  ) : (
                    <div className="w-5 h-5 mt-0.5 rounded bg-[#1E3F20] text-white grid place-items-center">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                  <div className="flex-1 line-through text-[#5C6B5E]">
                    {t.title}
                    {tab === "assigned" && (
                      <div className="text-[11px] normal-case no-underline mt-0.5">→ {users.find((u) => u.id === t.user_id)?.name || "user"}</div>
                    )}
                  </div>
                  {(!t.assigned_by || t.assigned_by === user?.id) && (
                    <button onClick={() => del(t.id)} className="text-red-600 p-1 hover:bg-red-50 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
